import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';
import { ClassItem, TeacherTimetable, TimetableState } from '@/types/timetable';
import { timetableFileStorageService, Timetable } from '@/services/db/timetable/fileStorageService';
import { universalStorage } from '@/services/storage/universalStorage';

export const useTimetableStore = create<TimetableState>()((set, get) => ({
  timetableData: [],
  
  fetchTimetableData: async (): Promise<TeacherTimetable[]> => {
    try {
      console.log('📅 Starting timetable data fetch with enhanced localStorage...');
      
      const timetables = await timetableFileStorageService.getAllTimetables();
      console.log(`📅 Retrieved ${timetables.length} timetables from enhanced storage`);
      
      const teachers = await universalStorage.getItem<any[]>('nalc_teachers') || [];
      console.log(`👨‍🏫 Retrieved ${teachers.length} teachers for mapping`);
      const teacherMap = new Map(teachers.map(t => [t.id.toString(), `${t.firstName} ${t.lastName}`]));
      
      // Get stored class status data with enhanced storage
      const classStatusData = await universalStorage.getItem<Record<string, any>>('nalc_class_status') || {};
      console.log(`📊 Retrieved class status data: ${Object.keys(classStatusData).length} entries`);
      
      const teacherTimetables: TeacherTimetable[] = [];
      const teacherClassMap = new Map<string, ClassItem[]>();
      
      // Process timetables with enhanced data handling
      for (const timetable of timetables) {
        // Use the timetable properties directly with fallback handling
        const teacherId = timetable.teacher ? timetable.teacher.toString() : 'unassigned';
        const teacherName = teacherId === 'unassigned' ? 'Unassigned' : (teacherMap.get(teacherId) || 'Unknown Teacher');
        
        if (!teacherClassMap.has(teacherId)) {
          teacherClassMap.set(teacherId, []);
        }
        
        // Get the stored status for this class, or default to 'present'
        const storedStatus = classStatusData[timetable.id] || { status: 'present', notes: '' };
        
        const classItem: ClassItem = {
          id: timetable.id,
          subject: timetable.subject || timetable.title,
          startTime: timetable.startTime,
          endTime: timetable.endTime,
          room: timetable.room || '',
          status: storedStatus.status,
          notes: storedStatus.notes || timetable.notes || '',
          alerted: false,
          teacherName: teacherName,
          teacherId: teacherId
        };
        
        teacherClassMap.get(teacherId)!.push(classItem);
      }
      
      // Build teacher timetables with enhanced data structure
      teacherClassMap.forEach((classes, teacherId) => {
        const teacherName = teacherId === 'unassigned' ? 'Unassigned Classes' : (teacherMap.get(teacherId) || 'Unknown Teacher');
        teacherTimetables.push({
          id: teacherId,
          name: teacherName,
          avatar: '',
          classes: classes.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
        });
      });
      
      console.log(`✅ Processed ${teacherTimetables.length} teacher timetables with enhanced storage`);
      set({ timetableData: teacherTimetables });
      
      return teacherTimetables;
    } catch (error) {
      console.error('❌ Failed to fetch timetable data:', error);
      set({ timetableData: [] });
      return [];
    }
  },
  
  createClass: async (teacherId, classData) => {
    try {
      console.log(`📅 Creating new class for teacher: ${teacherId} with enhanced storage...`);
      
      const teachers = await universalStorage.getItem<any[]>('nalc_teachers') || [];
      const teacher = teachers.find(t => t.id.toString() === teacherId);
      const teacherName = teacher ? `${teacher.firstName} ${teacher.lastName}` : 'Unassigned';
      
      const newTimetable: Omit<Timetable, 'id' | 'createdAt' | 'updatedAt'> = {
        title: classData.subject || 'New Class',
        description: classData.notes || '',
        type: 'Class',
        startTime: classData.startTime || new Date().toISOString(),
        endTime: classData.endTime || new Date().toISOString(),
        date: new Date(classData.startTime || new Date()).toISOString().split('T')[0],
        room: classData.room || '',
        teacher: teacherId === 'unassigned' ? undefined : teacherId,
        students: [],
        subject: classData.subject || '',
        status: 'Scheduled',
        notes: classData.notes || ''
      };
      
      const newEntry = await timetableFileStorageService.addTimetable(newTimetable);
      
      // Save the class status data separately with enhanced storage
      if (classData.status && classData.status !== 'present') {
        const classStatusData = await universalStorage.getItem<Record<string, any>>('nalc_class_status') || {};
        classStatusData[newEntry.id] = {
          status: classData.status,
          notes: classData.notes || '',
          updatedAt: new Date().toISOString()
        };
        await universalStorage.setItem('nalc_class_status', classStatusData);
        console.log(`💾 Saved class status with enhanced storage: ${newEntry.id} -> ${classData.status}`);
      }
      
      console.log(`✅ New class entry created with enhanced storage: ${newEntry.id} for teacher: ${teacherName}`);
      
      // Immediately refresh data to show the new class
      await get().fetchTimetableData();
      
    } catch (error) {
      console.error('❌ Failed to create class:', error);
      throw error;
    }
  },
  
  updateClass: async (teacherId, classId, classData) => {
    try {
      console.log(`📅 Updating class: ${classId} for teacher: ${teacherId} with enhanced storage...`);
      
      const updateData: Partial<Timetable> = {
        title: classData.subject || undefined,
        subject: classData.subject || undefined,
        startTime: classData.startTime || undefined,
        endTime: classData.endTime || undefined,
        room: classData.room || undefined,
        teacher: teacherId === 'unassigned' ? undefined : teacherId,
        notes: classData.notes || undefined
      };
      
      await timetableFileStorageService.updateTimetable(classId, updateData);
      
      // Update class status data separately with enhanced storage
      const classStatusData = await universalStorage.getItem<Record<string, any>>('nalc_class_status') || {};
      classStatusData[classId] = {
        status: classData.status || 'present',
        notes: classData.notes || '',
        updatedAt: new Date().toISOString()
      };
      await universalStorage.setItem('nalc_class_status', classStatusData);
      console.log(`💾 Updated class status with enhanced storage: ${classId} -> ${classData.status}`);
      
      // Force immediate refresh after update
      await get().fetchTimetableData();
      console.log('✅ Class updated successfully with enhanced storage and data refreshed');
      
    } catch (error) {
      console.error('❌ Failed to update class:', error);
      throw error;
    }
  },
  
  deleteClass: async (teacherId, classId) => {
    try {
      console.log(`📅 Deleting class: ${classId} with enhanced storage...`);
      
      await timetableFileStorageService.deleteTimetable(classId);
      
      // Remove class status data with enhanced storage
      const classStatusData = await universalStorage.getItem<Record<string, any>>('nalc_class_status') || {};
      delete classStatusData[classId];
      await universalStorage.setItem('nalc_class_status', classStatusData);
      console.log(`🗑️ Removed class status data with enhanced storage for: ${classId}`);
      
      // Force immediate refresh after deletion
      await get().fetchTimetableData();
      console.log('✅ Class deleted successfully with enhanced storage and data refreshed');
      
    } catch (error) {
      console.error('❌ Failed to delete class:', error);
      throw error;
    }
  },
  
  updateClassStatus: async (teacherId, classId, newStatus) => {
    try {
      console.log(`📅 Updating class status: ${teacherId}, ${classId}, ${newStatus} with enhanced storage...`);
      
      // Get and update class status data with enhanced storage
      const classStatusData = await universalStorage.getItem<Record<string, any>>('nalc_class_status') || {};
      classStatusData[classId] = {
        status: newStatus,
        notes: classStatusData[classId]?.notes || '',
        updatedAt: new Date().toISOString()
      };
      await universalStorage.setItem('nalc_class_status', classStatusData);
      console.log(`💾 Saved class status to enhanced storage: ${classId} -> ${newStatus}`);
      
      // Update the store state immediately for UI responsiveness
      set(state => {
        const updatedData = [...state.timetableData];
        const teacherIndex = updatedData.findIndex(t => t.id === teacherId);
        
        if (teacherIndex !== -1) {
          const classIndex = updatedData[teacherIndex].classes.findIndex(c => c.id === classId);
          
          if (classIndex !== -1) {
            updatedData[teacherIndex].classes[classIndex] = {
              ...updatedData[teacherIndex].classes[classIndex],
              status: newStatus
            };
            console.log(`🔄 Status updated in store with enhanced storage: ${newStatus}`);
          }
        }
        
        return { timetableData: updatedData };
      });
      
      // Also refresh the data to ensure consistency
      setTimeout(() => {
        get().fetchTimetableData();
      }, 100);
      
    } catch (error) {
      console.error('❌ Failed to update class status:', error);
      throw error;
    }
  }
}));
